import { daysBetween, phaseWeekNumber } from '~/calculations/calendar'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import { isOpinion } from '~/models/rulesOpinion'
import { ALGORITHM_IDS, DB_SCHEMA_VERSION, EXPORT_FORMAT_VERSION } from '~/models/schemaVersion'
import type { TrainingWeekRecord } from '~/models/trainingWeek'
import type { WeightEntryRecord } from '~/models/weightEntry'
import type { CalendarDate } from '~/types/dates'
import { ageInWholeYears, type ReportModel } from '../reportModel'
import { REVIEW_PROMPT } from '../reviewPrompt'
import { type ExportValue, excluded, gated, missing, present, presentOrMissing } from '../values'

export const EXPORT_FORMAT = 'project-sola-ai-review'

/**
 * Serialize a report model to the versioned AI-review JSON contract (D-039).
 *
 * Three properties this function exists to guarantee, each asserted by test:
 *
 *  1. **`appOpinion` appears before `reviewPrompt`.** D-008 makes the app's own
 *     opinion the first coaching opinion, and the reader is asked to critique
 *     it. Key order is how that ordering survives serialization, and JavaScript
 *     preserves insertion order for string keys, so the order below is the
 *     contract.
 *  2. **Nothing is a bare `null`.** Every absent field is `excluded` or
 *     `missing` with a reason.
 *  3. **Rounding happens here and nowhere else.** Storage and calculation keep
 *     full precision; a display-rounded figure appears only where a renderer
 *     needs to match, and is labelled separately.
 */
export function serializeWeeklyReport(model: ReportModel): Record<string, unknown> {
  const { selection, period, profile } = model

  return {
    format: EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    databaseSchemaVersion: DB_SCHEMA_VERSION,
    generatedAt: model.generatedAt,

    privacySelection: {
      scope: selection.scope,
      includePhotos: selection.includePhotos,
      includeStrengthScreenshots: selection.includeStrengthScreenshots,
      includeNotes: selection.includeNotes,
      includeExactDates: selection.includeExactDates,
    },

    reportingPeriod: {
      /* Exact dates are sensitive on their own — they place a person in time.
         The period still has to be interpretable, so when they are excluded the
         export states the shape of the window instead of its bounds. */
      from: gated(
        selection.includeExactDates,
        'privacySelection.includeExactDates',
        period.from as string | undefined,
        'fullHistoryHasNoLowerBound',
      ),
      to: gated(
        selection.includeExactDates,
        'privacySelection.includeExactDates',
        period.to as string,
        'noReferenceDate',
      ),
      scope: present(period.scope),
      weekStartDay: present(period.weekStartDay),
    },

    athleteProfile: serializeProfile(model),
    longTermObjective: presentOrMissing(profile.primaryGoal, 'notRecorded'),
    /* Before the phase, because a phase belongs to a project (D-062) and a
       reader meeting the phase first has no container to put it in. */
    project: serializeProject(model),
    currentPhase: serializePhase(model),
    goals: serializeGoal(model),

    weightEntries: model.weightEntries.map((entry) => serializeWeightEntry(entry, model)),
    weightTrends: serializeTrend(model),

    measurements: model.measurementSessions.map((session) => serializeSession(session, model)),
    bodyCompositionEstimates: serializeBodyComposition(model),

    /* Real from Checkpoint 4 (D-082). It was `excluded` unconditionally while
       no photo existed to include, which was honest then and would be a lie
       now. A reader must still be able to tell a deliberate omission from a
       forgotten one, which is why the excluded branch keeps its reason. */
    photos: serializePhotos(model),

    /* Version 2 (Checkpoint 2.7). Each of these was `missing` with a reason that
       had become false: the evidence has been recorded since 2.0–2.2 and the
       projection since 2.5, and only the export lagged. */
    strengthEntries: serializeStrengthEntries(model),
    strengthSummary: serializeStrengthSummary(model),
    recovery: serializeRecovery(model),
    adherence: serializeAdherence(model),
    projection: serializeProjection(model),
    phaseHistory: serializePhaseHistory(model),

    contextTags: serializeContextTags(model),
    notes: serializeNotes(model),

    /* The opinions the user was ACTUALLY GIVEN, week by week, each with the
       immutable phase snapshot it was judged against (D-046). Nothing here is
       recomputed: a past week reads as it read at the time, even if the phase
       has since been corrected (D-070). */
    weeklyReviews: model.weeklyReviews.map((entry) => serializeReview(entry, model)),

    // D-008: the app's opinion precedes the request for a second one.
    appOpinion: serializeOpinion(model),
    attachments: missing('notRecordedInThisVersion'),

    reviewPrompt: REVIEW_PROMPT,
  }
}

/**
 * Progress photos as metadata, never as bytes (D-082).
 *
 * **The bytes belong in the media ZIP**, and `mediaFile` is the link between the
 * two documents: a reader holding both can say which file is the front view from
 * week three. Without it the ZIP is a folder of anonymous JPEGs.
 *
 * Nothing here records where a photo was taken — the capture pipeline stripped
 * that before the record existed (D-081), so there is no location to gate.
 */
function serializePhotos(model: ReportModel): unknown {
  const { selection } = model
  if (!selection.includePhotos) return excluded('privacySelection.includePhotos')
  if (model.photos.length === 0) return missing('noPhotosInPeriod')

  return present(
    model.photos.map((photo) => ({
      view: present(photo.view),
      dayIndex: dayIndexOf(photo.capturedDate, model),
      capturedDate: gated(
        selection.includeExactDates,
        'privacySelection.includeExactDates',
        photo.capturedDate as string,
        'noCaptureDate',
      ),
      /* Which path produced it. A guided capture had the alignment overlay and
         a picked file did not, so a comparison across the two is less
         like-for-like than a reader would otherwise assume (D-081). */
      source: present(photo.source),
      widthPx: present(photo.widthPx, 'px'),
      heightPx: present(photo.heightPx, 'px'),
      byteSize: present(photo.byteSize, 'bytes'),
      /* Free text the user wrote, so it gates on notes exactly as a project
         name and a review note do. */
      note: gated(selection.includeNotes, 'privacySelection.includeNotes', photo.note, 'noNote'),
      mediaFile: present(mediaFileNameFor(photo)),
    })),
  )
}

/**
 * The name this photo takes inside the media ZIP.
 *
 * Derived from the record rather than from a counter, so the JSON and the ZIP
 * agree without either having to be built first. **The photo id is used rather
 * than the date**, because a file name is not gated by `includeExactDates` and
 * naming files `2026-08-07-front.jpg` would put exact dates in a ZIP directory
 * listing that the document itself carefully redacted.
 */
export function mediaFileNameFor(photo: { id: unknown; view: string }): string {
  return `photos/${String(photo.id)}-${photo.view}.jpg`
}

/**
 * Recovery for the most recent finalized week (D-066).
 *
 * The five items are phrased so **5 is always the better outcome**, and the
 * scale version travels with them — a later rewording means a new version, not
 * a silent reinterpretation of old numbers.
 */
function serializeRecovery(model: ReportModel): unknown {
  const check = latestReview(model)?.review.recovery
  if (check === undefined) return missing('noRecoveryCheckRecorded')

  return present({
    ratings: check.ratings,
    optionalRatings: check.optionalRatings ?? {},
    scaleVersion: check.scaleVersion,
    scaleDirection: 'higherIsBetter',
    notes: gated(
      model.selection.includeNotes,
      'privacySelection.includeNotes',
      check.notes,
      'noNotes',
    ),
  })
}

/** Adherence counts as recorded — never calories, never meals (D-060). */
function serializeAdherence(model: ReportModel): unknown {
  const check = latestReview(model)?.review.adherence
  if (check === undefined) return missing('noAdherenceCheckRecorded')

  return present({
    nutritionDaysOnPlan: check.nutritionDaysOnPlan,
    trainingSessionsPlanned: check.trainingSessionsPlanned,
    trainingSessionsCompleted: check.trainingSessionsCompleted,
    stepTargetDays: presentOrMissing(check.stepTargetDays, 'notRecorded', 'days'),
    proteinTargetDays: presentOrMissing(check.proteinTargetDays, 'notRecorded', 'days'),
    notes: gated(
      model.selection.includeNotes,
      'privacySelection.includeNotes',
      check.notes,
      'noNotes',
    ),
  })
}

/**
 * Estimated 1RM per lift, with the comparisons the decline rule reads (D-069).
 *
 * A week with no qualifying set is `missing` with its reason, never zero, and an
 * excluded set says why it did not count rather than vanishing.
 */
function serializeStrengthSummary(model: ReportModel): unknown {
  if (model.liftSummaries.length === 0) return missing('noSentinelLiftsDefined')

  return present(
    model.liftSummaries.map(({ lift, summary }) => ({
      name: lift.name,
      currentEstimate:
        summary.current.status === 'present'
          ? present(summary.current.estimateKg, 'kg', summary.current.algorithmId)
          : missing(summary.current.missingReason ?? 'noQualifyingSet'),
      excludedSets: summary.current.excludedSets.map((set) => ({
        reps: set.reps,
        reason: set.reason,
      })),
      previousWeekKg: presentOrMissing(summary.previousWeekKg, 'noFigureLastWeek', 'kg'),
      fourWeekAverageKg: presentOrMissing(summary.fourWeekAverageKg, 'noFiguresInWindow', 'kg'),
      weeksInFourWeekAverage: summary.weeksInFourWeekAverage,
      changeVsFourWeekPercent: presentOrMissing(
        summary.changeVsFourWeekPercent,
        'noFiguresToCompare',
        '%',
      ),
      relativeToBodyWeight: presentOrMissing(summary.relativeToBodyWeight, 'noBodyWeightRecorded'),
    })),
    undefined,
    ALGORITHM_IDS.estimated1RM,
  )
}

function serializeStrengthEntries(model: ReportModel): unknown {
  if (model.strengthEntries.length === 0) return missing('noSetsRecordedInPeriod')

  return present(
    model.strengthEntries.map((entry) => ({
      liftId: entry.liftId as string,
      dayIndex: dayIndexOf(entry.date, model),
      date: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        entry.date as string,
        'noDate',
      ),
      loadKg: present(entry.loadKg as number, 'kg'),
      reps: entry.reps,
    })),
  )
}

/**
 * The four projection paths (D-068).
 *
 * **A week span and an approximate month, never a date**, and never anything
 * about appearance. The month has no day in it to serialize.
 */
function serializeProjection(model: ReportModel): unknown {
  const projection = model.projection
  if (projection.kind === 'unavailable') return missing(projection.reason)
  if (projection.kind === 'alreadyWithinTarget') {
    return present({
      state: 'alreadyWithinTarget',
      anchorKg: present(projection.anchorKg as number, 'kg'),
      algorithm: projection.algorithm,
    })
  }

  return present(
    {
      state: 'projected',
      anchorKg: present(projection.anchorKg as number, 'kg'),
      targetWeightKg: present(projection.target.weightKg as number, 'kg'),
      direction: projection.target.direction,
      weekSpan:
        projection.span === undefined
          ? missing('noPlanPathReachesTheTarget')
          : present(projection.span, 'weeks'),
      paths: [
        ...projection.plan,
        ...(projection.observed === undefined ? [] : [projection.observed]),
      ].map((path) => ({
        path: path.key,
        weeklyRatePercent: present(path.weeklyRatePercent, '% of body weight per week'),
        outcome: path.outcome.kind,
        weeks:
          path.outcome.kind === 'reaches'
            ? present(path.outcome.weeks, 'weeks')
            : missing(path.outcome.kind),
        approximateMonth:
          path.outcome.kind === 'reaches'
            ? present(path.outcome.approximateMonth)
            : missing(path.outcome.kind),
      })),
      note: 'An estimate from recent weigh-ins. It is not a schedule, carries no calendar date, and describes weight only.',
    },
    undefined,
    projection.algorithm,
  )
}

/**
 * Phases and their corrections (D-070).
 *
 * A corrected date is shown **as corrected**, with what it replaced. Presenting
 * a late amendment as though it were the original record is exactly what D-070
 * refused when it rejected a silent overwrite.
 */
function serializePhaseHistory(model: ReportModel): unknown {
  if (model.phases.length === 0) return missing('noPhasesRecorded')

  return present(
    model.phases.map((phase) => ({
      type: phase.type,
      status: phase.status,
      startDate: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        phase.startDate as string,
        'noStartDate',
      ),
      actualEndDate: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        phase.actualEndDate as string | undefined,
        'phaseHasNotEnded',
      ),
      targetWeeklyChangePercent: present(
        { min: phase.targetWeeklyChangeMinPercent, max: phase.targetWeeklyChangeMaxPercent },
        '% of body weight per week',
      ),
      transitionReason: presentOrMissing(phase.transitionReason, 'noReasonRecorded'),
      corrections: (phase.corrections ?? []).map((correction) => ({
        field: correction.field,
        /* The replaced value is the point of the record, but a date is still a
           date: exclusion applies to it exactly as it does elsewhere. */
        from: gated(
          model.selection.includeExactDates || !isDateField(correction.field),
          'privacySelection.includeExactDates',
          correction.from,
          'fieldWasEmpty',
        ),
        to: gated(
          model.selection.includeExactDates || !isDateField(correction.field),
          'privacySelection.includeExactDates',
          correction.to,
          'fieldWasCleared',
        ),
      })),
    })),
  )
}

function isDateField(field: string): boolean {
  return field === 'startDate' || field === 'actualEndDate'
}

/** One finalized week, with the opinion it was given rather than a fresh one. */
function serializeReview(entry: ReportModel['weeklyReviews'][number], model: ReportModel): unknown {
  const { review, opinion } = entry

  return {
    dayIndex: dayIndexOf(review.weekStartDate, model),
    weekStartDate: gated(
      model.selection.includeExactDates,
      'privacySelection.includeExactDates',
      review.weekStartDate as string,
      'noWeekStart',
    ),
    phaseSnapshot:
      review.phaseSnapshot === undefined
        ? missing('noPhaseSnapshot')
        : present({
            phaseType: review.phaseSnapshot.phaseType,
            weekNumber: review.phaseSnapshot.weekNumber,
            targetWeeklyChangePercent: {
              min: review.phaseSnapshot.targetWeeklyChangeMinPercent,
              max: review.phaseSnapshot.targetWeeklyChangeMaxPercent,
            },
          }),
    planSnapshot: serializePlanSnapshot(review.planSnapshot, model),
    trainingWeek: serializeTrainingWeek(entry.trainingWeek, model),
    contextTags: present(review.contextTags),
    notes: gated(
      model.selection.includeNotes,
      'privacySelection.includeNotes',
      review.notes,
      'noNotes',
    ),
    opinionGiven:
      opinion === undefined
        ? missing('noOpinionStored')
        : present({
            category: opinion.opinion.category,
            primaryAction: opinion.opinion.primaryAction,
            confidence: opinion.opinion.confidence,
            confidenceReason: opinion.opinion.confidenceReason,
            triggeredRules: opinion.opinion.triggeredRules,
            engineVersion: opinion.opinion.engineVersion,
          }),
  }
}

/**
 * What the week was actually planned to contain (D-099, as amended at C6.5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE ANCHORS ARE OMITTED UNCONDITIONALLY, AND THIS IS THE ONLY GATE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * D-099 §B says a routine anchor is **never** in an AI-review export: it says
 * nothing about the body and everything about when the user is tired of being
 * asked. The snapshot stores them anyway, so historical UI can show them — which
 * means the omission has to happen here.
 *
 * **Not behind `includeNotes`, not behind `includeExactDates`, not behind any
 * future flag.** It is not a privacy preference the user can weigh up; it is a
 * field that has no business in this document at all. A test asserts the
 * serialized text contains no anchor string.
 *
 * Putting the gate here rather than in a renderer is what keeps D-085 intact:
 * the PDF and the CSV read this output, so a field absent from the JSON is
 * absent from all three by construction. A renderer that filtered anchors itself
 * would be a second guard for one rule with only one of them load-bearing.
 *
 * **`missing` rather than an empty object for a review finalized before C6.5.**
 * Those carry no snapshot and are never backfilled, so the honest answer is that
 * the plan for that week was not recorded — not that it was empty.
 */
function serializePlanSnapshot(
  snapshot: ReportModel['weeklyReviews'][number]['review']['planSnapshot'],
  model: ReportModel,
): unknown {
  if (snapshot === undefined) return missing('noPlanSnapshotRecordedForThisWeek')

  return present({
    activeDomains: snapshot.activeDomains,
    goals: snapshot.goals.map((goal) => ({
      domain: goal.domain,
      intention: goal.intention,
      priority: goal.priority,
      standard: plannedWeek(goal.standard, model),
      minimumViable: plannedWeek(goal.minimumViable, model),
      schedule:
        goal.schedule === undefined
          ? missing('noScheduleRecordedForThisGoal')
          : present(
              goal.schedule.map((session) => ({
                /* A weekday is plan structure, not a date, so it is not behind
                   `includeExactDates` — "Mondays" says nothing about when this
                   user did anything. The name is free text they wrote and is
                   gated exactly as the project name and a review note are. */
                weekday: session.weekday,
                name: gated(
                  model.selection.includeNotes,
                  'privacySelection.includeNotes',
                  session.name,
                  'noSessionName',
                ),
                expectedMinutes: presentOrMissing(session.expectedMinutes, 'noExpectedDuration'),
              })),
            ),
    })),
    nutrition:
      snapshot.nutrition === undefined
        ? missing('noNutritionPlanThatWeek')
        : present({
            source: snapshot.nutrition.source,
            /* Free text the user typed — "MacroFactor", a coach's first name —
               so it gates on notes exactly as the project name and a review note
               do. This is the export-gating rule in its newest costume. */
            providerLabel: gated(
              model.selection.includeNotes,
              'privacySelection.includeNotes',
              snapshot.nutrition.providerLabel,
              'noProviderLabel',
            ),
            calorieTarget: presentOrMissing(snapshot.nutrition.calorieTarget, 'noCalorieTarget'),
            calorieTargetBasis: presentOrMissing(
              snapshot.nutrition.calorieTargetBasis,
              'noCalorieTarget',
            ),
            proteinTargetG: presentOrMissing(snapshot.nutrition.proteinTargetG, 'noProteinTarget'),
          }),
    /* `snapshot.anchors` is deliberately not read. See the note above. */
    rulesVersion: snapshot.rulesVersion,
  })
}

/**
 * A planned week, with its labels gated.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A GATE THAT COVERS ONE OF TWO COPIES IS NOT A GATE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `standard` is **derived from the schedule** for a goal that has one (D-105
 * §3), so its labels *are* session names — the same strings the schedule block
 * gates on `includeNotes`. Leaving them ungated here would have let *"Coach
 * Marnie session"* out through the plan while the schedule dutifully withheld
 * it, which is a gate with the door open beside it. Found by a fixture whose
 * session name could actually embarrass someone; a neutral fixture would have
 * passed.
 *
 * **So the label gates everywhere, for a quota goal too.** It was always free
 * text the user typed, and this is the export-gating rule in the newest costume
 * the project keeps predicting. **The counts do not gate**: `timesPerWeek` and
 * `minutes` are what a reviewer actually reads, and they say how much was
 * planned without saying what the user called it.
 */
function plannedWeek(
  items:
    | readonly {
        readonly label: string
        readonly timesPerWeek: number
        readonly minutes?: number
      }[]
    | undefined,
  model: ReportModel,
): unknown {
  if (items === undefined) return missing('noWeeklyPlanRecorded')

  return present(
    items.map((item) => ({
      label: gated(
        model.selection.includeNotes,
        'privacySelection.includeNotes',
        item.label,
        'noPlannedItemLabel',
      ),
      timesPerWeek: item.timesPerWeek,
      minutes: presentOrMissing(item.minutes, 'noTimeEstimate'),
    })),
  )
}

/**
 * The week as it was executed (D-105 §12).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  FOUR STATES, AND `undefined` IS THE ONE THAT MATTERS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * A session's `outcome` is `completed`, `skipped`, or **not recorded**. This
 * emits `notRecorded` for the third rather than omitting the session or calling
 * it missed: it was planned, nothing was said about it, and D-105 §5 is explicit
 * that missing is never converted into a zero or a judgement. A reader — human
 * or model — must be able to tell "the user skipped it" from "the user said
 * nothing", because those are different weeks.
 *
 * **No ratio and no total.** The sessions are listed and counted by whoever
 * reads them; `adherence` already carries the two numbers, separately.
 *
 * The gates, all of which live here and nowhere else (D-085):
 * - **`name` and a skip's `reason` gate on `includeNotes`** — free text the user
 *   wrote, and people name a session after an injury, a physiotherapist or a
 *   person. Same rule as the project name and a photo note (D-080).
 * - **`plannedDate` and `scheduledDate` gate on `includeExactDates`**, with day
 *   indexes that survive redaction so the order and the gap between "meant to
 *   be" and "ended up" are still readable. The media manifest does the same.
 */
function serializeTrainingWeek(week: TrainingWeekRecord | undefined, model: ReportModel): unknown {
  if (week === undefined) return missing('noTrainingWeekRecordedForThisWeek')

  return present({
    sessions: week.sessions.map((session) => ({
      domain: session.domain,
      origin: session.origin,
      name: gated(
        model.selection.includeNotes,
        'privacySelection.includeNotes',
        session.name,
        'noSessionName',
      ),
      expectedMinutes: presentOrMissing(session.expectedMinutes, 'noExpectedDuration'),
      plannedDayIndex: dayIndexOf(session.plannedDate, model),
      scheduledDayIndex: dayIndexOf(session.scheduledDate, model),
      plannedDate: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        session.plannedDate as string,
        'noSessionDate',
      ),
      scheduledDate: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        session.scheduledDate as string,
        'noSessionDate',
      ),
      /* Rescheduling history is a count, not a list of dates. How many times a
         session moved is legible; every date it passed through is the calendar
         `includeExactDates` exists to withhold. */
      timesMoved: session.changes.length,
      outcome: session.outcome === undefined ? 'notRecorded' : session.outcome.kind,
      skipReason:
        session.outcome?.kind === 'skipped'
          ? gated(
              model.selection.includeNotes,
              'privacySelection.includeNotes',
              session.outcome.reason,
              'noSkipReason',
            )
          : missing('notSkipped'),
    })),
  })
}

/** The most recent finalized review in the period, if any. */
function latestReview(model: ReportModel): ReportModel['weeklyReviews'][number] | undefined {
  return model.weeklyReviews.at(-1)
}

/**
 * Days from the earliest observation in the report.
 *
 * Anchored to the data rather than to the period start, so it is defined for
 * full-history exports too, which have no lower bound.
 */
function dayIndexOf(date: CalendarDate, model: ReportModel): number {
  const earliest = earliestObservation(model)
  return earliest === undefined ? 0 : daysBetween(earliest, date)
}

function earliestObservation(model: ReportModel): CalendarDate | undefined {
  const dates = [
    ...model.weightEntries.map((entry) => entry.date),
    ...model.measurementSessions.map((session) => session.date),
  ]
  return dates.length === 0 ? undefined : dates.reduce((a, b) => (a <= b ? a : b))
}

function serializeProfile(model: ReportModel) {
  const { profile, selection, reference } = model

  return {
    // D-038: age is derived; the date of birth itself is omitted entirely
    // unless exact dates were explicitly included.
    age: present(ageInWholeYears(profile.dateOfBirth, reference), 'years'),
    dateOfBirth: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      profile.dateOfBirth as string,
      'notRecorded',
    ),
    heightCm: present(profile.heightCm as number, 'cm'),
    // Version 1 is men-only, and the formula depends on it (D-013).
    formulaContext: present('male'),
    trainingExperience: presentOrMissing(profile.trainingExperience, 'notRecorded'),
    preferredStyle: presentOrMissing(profile.preferredStyle, 'notRecorded'),
    constraints: presentOrMissing(profile.constraints, 'notRecorded'),
    avoidMultipleWeeklyChanges: present(profile.avoidMultipleWeeklyChanges),
  }
}

/**
 * The project block (D-062).
 *
 * Small on purpose: name, outcome, week N of M and the target date. Everything
 * a reader needs to know which undertaking this week belongs to, and nothing
 * about the months either side of it. The full history is the completion
 * summary, which is a separate user-initiated export.
 *
 * **The name is a free-text field the user wrote**, so it gates on
 * `includeNotes` — someone may well have called a project after a wedding, a
 * holiday or a person. The dates gate on `includeExactDates`, as every other
 * date in this document does. Gating one and not the other is exactly the
 * silent narrowing the export rules warn about.
 */
function serializeProject(model: ReportModel) {
  const { project, selection } = model
  if (!project) return missing('noActiveProject')

  return {
    name: gated(selection.includeNotes, 'privacySelection.includeNotes', project.name, 'notNamed'),
    desiredOutcome: presentOrMissing(project.desiredOutcome, 'predatesProjectSetup'),
    status: present(project.status),
    weekNumber: present(phaseWeekNumber(project.startDate, model.reference), 'projectWeeks'),
    totalPlannedWeeks: present(
      Math.ceil((daysBetween(project.startDate, project.targetDate) + 1) / 7),
      'weeks',
    ),
    startDate: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      project.startDate as string,
      'notRecorded',
    ),
    targetDate: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      project.targetDate as string,
      'notRecorded',
    ),
    /* The route Project Sola recommended at setup, and whether it disagreed
       with what was asked for. This is the D-008 opinion at project scale, and
       it belongs in an export whose whole purpose is a second opinion. */
    recommendedRoute:
      project.recommendationSnapshot === undefined
        ? missing('predatesTheRouteEngine')
        : present({
            phase: project.recommendationSnapshot.recommendedPhase,
            reasoning: project.recommendationSnapshot.reasoning,
            redirectedFrom: presentOrMissing(
              project.recommendationSnapshot.redirect?.insteadOf,
              'noRedirect',
            ),
            confidence: project.recommendationSnapshot.confidence,
          }),
  }
}

function serializePhase(model: ReportModel) {
  const { phase, selection } = model
  if (!phase) return missing('noActivePhase')

  return {
    type: present(phase.type),
    /* Inclusive and anchored to the phase start, so the start date is week 1
       day 1 (D-034). Distinct from the calendar week that drives export scope,
       and never conflated with it. */
    weekNumber: present(phaseWeekNumber(phase.startDate, model.reference), 'phaseWeeks'),
    totalPlannedWeeks: present(
      Math.ceil((daysBetween(phase.startDate, phase.plannedEndDate) + 1) / 7),
      'weeks',
    ),
    startDate: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      phase.startDate as string,
      'notRecorded',
    ),
    plannedEndDate: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      phase.plannedEndDate as string,
      'notRecorded',
    ),
    targetWeeklyChangeMinPercent: present(phase.targetWeeklyChangeMinPercent, 'percentPerWeek'),
    targetWeeklyChangeMaxPercent: present(phase.targetWeeklyChangeMaxPercent, 'percentPerWeek'),
    status: present(phase.status),
  }
}

function serializeGoal(model: ReportModel) {
  const { goal, selection } = model
  if (!goal) return missing('noGoalRecorded')

  return {
    targetDate: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      goal.targetDate as string,
      'notRecorded',
    ),
    targetWeightMinKg: presentOrMissing(
      goal.targetWeightMinKg as number | undefined,
      'notSet',
      'kg',
    ),
    targetWeightMaxKg: presentOrMissing(
      goal.targetWeightMaxKg as number | undefined,
      'notSet',
      'kg',
    ),
    targetWaistMinCm: presentOrMissing(goal.targetWaistMinCm as number | undefined, 'notSet', 'cm'),
    targetWaistMaxCm: presentOrMissing(goal.targetWaistMaxCm as number | undefined, 'notSet', 'cm'),
    desiredVisualOutcome: presentOrMissing(goal.desiredVisualOutcome, 'notSet'),
    maxStrengthDeclinePercent: presentOrMissing(
      goal.maxStrengthDeclinePercent,
      'notSet',
      'percent',
    ),
  }
}

function serializeWeightEntry(entry: WeightEntryRecord, model: ReportModel) {
  const { selection } = model

  return {
    date: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      entry.date as string,
      'notRecorded',
    ),
    /* Always present, whatever the date setting.
       Excluding exact dates must not destroy the meaning of the data
       (export-format.md): a list of weights with no dates has no sequence and
       no spacing, so no trend can be read from it. A day index relative to the
       first observation preserves both while disclosing no calendar date. */
    dayIndex: present(dayIndexOf(entry.date, model), 'daysFromFirstObservation'),
    weightKg: present(entry.weightKg as number, 'kg'),
    note: gated(selection.includeNotes, 'privacySelection.includeNotes', entry.note, 'noNote'),
    contextTags: present(entry.contextTags),
    corrected: present(entry.correctedFromKg !== undefined),
  }
}

function serializeTrend(model: ReportModel) {
  const { current, previous, comparison } = model.weightTrend

  return {
    currentWindow: {
      entryCount: present(current.entryCount, 'entries'),
      sufficiency: present(current.sufficiency),
      averageKg: presentOrMissing(
        current.averageKg as number | undefined,
        'insufficientEntries',
        'kg',
        ALGORITHM_IDS.trend,
      ),
    },
    previousWindow: {
      entryCount: present(previous.entryCount, 'entries'),
      sufficiency: present(previous.sufficiency),
      averageKg: presentOrMissing(
        previous.averageKg as number | undefined,
        'insufficientEntries',
        'kg',
        ALGORITHM_IDS.trend,
      ),
    },
    weekOnWeek: comparison
      ? {
          changeKg: present(comparison.changeKg as number, 'kg', ALGORITHM_IDS.trend),
          percentChange: present(
            comparison.percentChange as number,
            'percentPerWeek',
            ALGORITHM_IDS.trend,
          ),
        }
      : missing('bothWindowsMustBeSufficientToCompare'),
  }
}

function serializeSession(session: MeasurementSessionRecord, model: ReportModel) {
  const { selection } = model

  const sites = Object.entries(session.sites).map(([key, reading]) => ({
    site: key,
    medianCm: present(reading?.medianCm ?? 0, 'cm'),
    spreadCm: present(reading?.spreadCm ?? 0, 'cm'),
    exceedsThreshold: present(reading?.exceedsThreshold ?? false),
    thresholdUsedCm: present(reading?.thresholdUsedCm ?? 0, 'cm'),
    /* All three readings are retained in storage (D-045), and the export
       carries them so measurement quality can be judged rather than taken on
       trust. */
    readings: present(reading?.readings ?? [], 'cm'),
  }))

  return {
    date: gated(
      selection.includeExactDates,
      'privacySelection.includeExactDates',
      session.date as string,
      'notRecorded',
    ),
    // Same reasoning as weight entries: sequence must survive redaction.
    dayIndex: present(dayIndexOf(session.date, model), 'daysFromFirstObservation'),
    sites,
    conditionNote: gated(
      selection.includeNotes,
      'privacySelection.includeNotes',
      session.conditionNote,
      'noNote',
    ),
  }
}

function serializeBodyComposition(model: ReportModel) {
  const latest = model.measurementSessions.at(-1)
  if (!latest) return missing('noMeasurementSessionRecorded')

  const derived = latest.derived

  return {
    /* The label is fixed by D-014 and travels with the value, so a reader can
       never mistake this for a clinical measurement or a compliance result. */
    label: present('Navy circumference estimate for men'),
    algorithm: present(ALGORITHM_IDS.bodyFat),
    status: present(derived.bodyFatStatus),
    bodyFatPercent: presentOrMissing(
      derived.bodyFatPercent,
      derived.bodyFatInvalidReason ?? 'notCalculable',
      'percent',
      ALGORITHM_IDS.bodyFat,
    ),
    waistToHeightRatio: presentOrMissing(derived.waistToHeightRatio, 'notCalculable', 'ratio'),
    fatMassKg: presentOrMissing(derived.fatMassKg, 'notCalculable', 'kg', ALGORITHM_IDS.bodyFat),
    leanMassKg: presentOrMissing(derived.leanMassKg, 'notCalculable', 'kg', ALGORITHM_IDS.bodyFat),
    disclaimer: present(
      'This is a longitudinal personal-tracking estimate, not an official military compliance result, a clinical assessment, or an exact body-fat measurement.',
    ),
  }
}

function serializeContextTags(model: ReportModel) {
  const tags = model.weightEntries.flatMap((entry) => entry.contextTags)
  return present(Array.from(new Set(tags)).sort())
}

function serializeNotes(model: ReportModel) {
  if (!model.selection.includeNotes) return excluded('privacySelection.includeNotes')

  const notes = model.weightEntries
    .filter((entry) => entry.note !== undefined)
    .map((entry) => ({
      date: gated(
        model.selection.includeExactDates,
        'privacySelection.includeExactDates',
        entry.date as string,
        'notRecorded',
      ),
      note: present(entry.note as string),
    }))

  return notes.length === 0 ? missing('noNotesRecorded') : present(notes)
}

/**
 * The app's own opinion.
 *
 * `PhaseRulesUnavailable` is **never exported as an opinion** (D-043). It is
 * not a recommendation, so it serializes as a missing value naming the reason,
 * and no category, action or confidence appears anywhere.
 */
function serializeOpinion(model: ReportModel): ExportValue<Record<string, unknown>> {
  const outcome = model.outcome

  if (!isOpinion(outcome)) {
    return missing(
      outcome.outcome === 'phaseRulesUnavailable'
        ? 'recommendationsAreNotAvailableForThisPhaseInThisBuild'
        : 'noActivePhase',
    )
  }

  return present({
    category: outcome.category,
    primaryAction: outcome.primaryAction,
    supportingActions: outcome.supportingActions,
    confidence: outcome.confidence,
    confidenceReason: outcome.confidenceReason,
    evidence: outcome.evidence.map((item) => ({
      kind: item.kind,
      statement: item.statement,
      value: item.value === undefined ? missing('notNumeric') : present(item.value, item.unit),
    })),
    watchItems: outcome.watchItems,
    missingEvidence: outcome.missingEvidence,
    rejectedAlternatives: outcome.rejectedAlternatives,
    triggeredRules: outcome.triggeredRules,
    engineVersion: outcome.engineVersion,
    evaluatedAt: outcome.evaluatedAt,
    /* Stated in the export itself, not only in the docs. A reader must not take
       this for the finished Version 1 engine (D-043). */
    /* Version 2 (Checkpoint 2.7). The note may finally say the engine is
       complete, because D-043's four conditions now hold and are checked
       mechanically by `rules/completeness.test.ts`. It still says what Version 1
       does NOT yet have, because the engine being complete and the app being
       finished are different claims and a reader must not conflate them. */
    coverageNote:
      'All five Version 1 phase families are implemented and this week was judged by the one for its phase, on weight, waist, recovery, adherence and sentinel-lift strength. Photos are not yet captured by this app, and the estimates here remain estimates rather than measurements or diagnoses.',
  })
}
